import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCloseOutline } from "react-icons/io5";
import Popup from "src/components/Popup/Popup";
import { useApi } from "src/hooks/useApi";

interface ExclusionRule {
    nr: string;
    excludesWithCodes: string[];
    note: string;
}

interface MaxCountRule {
    nr: string;
    maxCount: number;
    requiresAnyOf?: string[] | null;
    note: string;
}

export function GoaRulesPopup({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const { data, loading, error } = useApi<{ rules: ExclusionRule[]; maxCountRules: MaxCountRule[] }>("/goa/exclusion-rules");
    const rules = data?.rules ?? [];
    const maxCountRules = data?.maxCountRules ?? [];

    const searching = search.trim().length > 0;
    const filteredRules = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rules;
        return rules.filter(
            (r) => r.nr.toLowerCase().includes(term) || r.excludesWithCodes.some((c) => c.toLowerCase().includes(term)),
        );
    }, [rules, search]);

    const filteredMaxCountRules = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return maxCountRules;
        return maxCountRules.filter(
            (r) => r.nr.toLowerCase().includes(term) || (r.requiresAnyOf ?? []).some((c) => c.toLowerCase().includes(term)),
        );
    }, [maxCountRules, search]);

    // Each exclusion pair is stored symmetrically (5→[6,7,8] and 6→[5,7,8] etc.) so the
    // conflict-checker can look up either code — dedupe by note text so the list reads
    // as distinct rules, not near-duplicate entries for the same group.
    const uniqueNotes = useMemo(() => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const r of filteredRules) {
            if (seen.has(r.note)) continue;
            seen.add(r.note);
            out.push(r.note);
        }
        for (const r of filteredMaxCountRules) {
            if (seen.has(r.note)) continue;
            seen.add(r.note);
            out.push(r.note);
        }
        return out;
    }, [filteredRules, filteredMaxCountRules]);

    return (
        <Popup
            className="popup--wide"
            title={t("invoicePage.goaRulesTitle", { defaultValue: "GOÄ-Ausschlussregeln" })}
            onClose={onClose}
            message={
                <div className="goa-rules-popup">
                    <p className="goa-rules-popup__intro">
                        {t("invoicePage.goaRulesIntro", {
                            defaultValue: "Diese GOÄ-Ziffern dürfen laut Gebührenordnung nicht zusammen am selben Behandlungstag abgerechnet werden. Aktuell abgedeckt: Abschnitte B, C, E–O.",
                        })}
                    </p>
                    <div className="goa-catalog-popup__search-wrap">
                        <input
                            type="text"
                            className="goa-catalog-popup__search"
                            placeholder={t("invoicePage.goaRulesSearchPlaceholder", { defaultValue: "Nach GOÄ-Ziffer suchen…" })}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                        {searching && (
                            <IoCloseOutline
                                className="goa-catalog-popup__search-clear"
                                onClick={() => setSearch("")}
                                role="button"
                                aria-label={t("form.clear", { defaultValue: "Clear" })}
                            />
                        )}
                    </div>
                    {loading ? (
                        <div className="goa-catalog-popup__list-loading">
                            <div className="loader-spinner" />
                        </div>
                    ) : error ? (
                        <p className="goa-catalog-popup__empty goa-catalog-popup__empty--error">{error}</p>
                    ) : !uniqueNotes.length ? (
                        <p className="goa-catalog-popup__empty">{t("invoicePage.goaRulesEmpty", { defaultValue: "Keine Regeln gefunden." })}</p>
                    ) : (
                        <ul className="goa-rules-popup__list">
                            {uniqueNotes.map((note, i) => (
                                <li key={i}>{note}</li>
                            ))}
                        </ul>
                    )}
                    <p className="goa-rules-popup__count">
                        {!loading && !error && uniqueNotes.length > 0 &&
                            t("invoicePage.goaRulesCount", { count: uniqueNotes.length, defaultValue: "{{count}} Regeln" })}
                    </p>
                </div>
            }
            actions={[{ label: t("popup.cancel"), variant: "default", onClick: onClose }]}
        />
    );
}
